import React, { useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import BasePlot from "./BasePlot";

const Plot = createPlotlyComponent(Plotly);

export interface BubblePlotProps {
  data: any[];
  xMetric?: string; // label only; actual x is ratio
  yMetric?: string;
  sizeMetric?: string;
  colorMetric?: string;
  title?: string;
  className?: string;
  plotRef?: string;
}

const BubblePlot = ({
  data,
  xMetric = "Measured Genes",
  yMetric = "Pathway",
  sizeMetric = "Pathway Size",
  colorMetric = "FDR",
  title = "Bubble Plot",
  className = "",
  plotRef,
}: BubblePlotProps): React.ReactElement => {
  const [colorScale] = useState("Viridis");
  const [rangeFilter] = useState("All");

  if (!data || data.length === 0) {
    console.warn("[BubblePlot] No data provided.");
    return <p>No enrichment data available.</p>;
  }

  console.log("[BubblePlot] raw data length:", data.length);
  console.log("[BubblePlot] raw data sample:", data[0]);

  // ---------- preprocess & filter -------------------------------------------

  type Row = {
    raw: any;
    pathway: string;
    measured: number;
    pathwaySize: number;
    bubbleSize: number;
    pValue: number; // FDR (or P-value if you swap colorMetric)
    ratio: number; // reach factor = measured / pathwaySize
  };

  const parsePValue = (raw: any): number => {
    if (typeof raw === "number") {
      return raw;
    }
    if (typeof raw === "string") {
      let s = raw.trim();

      // Replace "×10^-10" or "x10^-10" with "e-10"
      s = s.replace(/×10\^?(-?\d+)/i, "e$1"); // "3×10^-10" -> "3e-10"
      s = s.replace(/x10\^?(-?\d+)/i, "e$1"); // "3x10^-10" -> "3e-10"

      const n = Number(s);
      if (Number.isFinite(n)) {
        return n;
      }
    }

    // Fallback: treat unparseable as tiny but non-zero
    return 1e-6;
  };

  let rows: Row[] = data.map((d, i) => {
    // Try multiple key variants, just in case backend renamed columns
    const measuredRaw =
      d["Measured Genes"] ??
      d["Measured_Genes"] ??
      d.measured_genes ??
      d.measuredGenes ??
      d[xMetric];

    const pathwaySizeRaw =
      d["Pathway Size"] ??
      d["Pathway_Size"] ??
      d.pathway_size ??
      d[sizeMetric];

    const bubbleSizeRaw =
      d[sizeMetric] ??
      d["Pathway Size"] ??
      d["Pathway_Size"] ??
      d.pathway_size;

    const measuredNum = Number(measuredRaw);
    const pathwaySizeNum = Number(pathwaySizeRaw);
    const bubbleSizeNum = Number(bubbleSizeRaw);

    const measured = Number.isFinite(measuredNum) ? measuredNum : 0;
    const pathwaySize = Number.isFinite(pathwaySizeNum) ? pathwaySizeNum : 0;
    const bubbleSize = Number.isFinite(bubbleSizeNum) ? bubbleSizeNum : 0;

    const rawP = d[colorMetric];
    let pValue = parsePValue(rawP);
    if (!Number.isFinite(pValue) || pValue <= 0) {
      pValue = 1e-12;
    }

    const ratio = pathwaySize > 0 ? measured / pathwaySize : 0;

    if (i === 0) {
      console.log("[BubblePlot] parsed first row:", {
        measuredRaw,
        pathwaySizeRaw,
        bubbleSizeRaw,
        measured,
        pathwaySize,
        bubbleSize,
        pValue,
        ratio,
        keys: Object.keys(d),
      });
    }

    return {
      raw: d,
      pathway: String(d[yMetric] ?? d.Pathway ?? d.pathway),
      measured,
      pathwaySize,
      bubbleSize,
      pValue,
      ratio,
    };
  });

  console.log("[BubblePlot] parsed rows length:", rows.length);

  // filter: Measured Genes > 1 and FDR < 0.05
  const filteredByThreshold = rows.filter(
    (r) => r.measured > 1 && r.pValue < 0.05,
  );

  console.log(
    "[BubblePlot] rows after Measured>1 & FDR<0.05:",
    filteredByThreshold.length,
  );

  // Fallback: if nothing passes the filter, show all rows instead of blank
  if (!filteredByThreshold.length) {
    console.warn(
      "[BubblePlot] No rows passed Measured Genes > 1 && FDR < 0.05. Showing all rows instead.",
    );
  } else {
    rows = filteredByThreshold;
  }

  if (rows.length === 0) {
    console.error("[BubblePlot] rows length is 0 after parsing & fallback.");
    return <p>No enrichment rows available.</p>;
  }

  // rank by reach factor (ratio), descending
  const sortedRows = rows.slice().sort((a, b) => b.ratio - a.ratio);

  // optional “range” filter on bubble size
  let filteredRows = sortedRows;
  if (rangeFilter !== "All") {
    const [min, max] = rangeFilter.split("-").map(Number);
    filteredRows = sortedRows.filter(
      (r) => r.bubbleSize >= min && r.bubbleSize <= max,
    );
  }

  console.log("[BubblePlot] rows after range filter:", filteredRows.length);

  if (filteredRows.length === 0) {
    return (
      <p>
        No pathways pass the size range filter after applying Measured Genes &gt; 1 and
        FDR &lt; 0.05.
      </p>
    );
  }

  // ---------- build arrays for Plotly ---------------------------------------

  const xData = filteredRows.map((r) => r.ratio);
  const yData = filteredRows.map((r) => r.pathway);
  const sizes = filteredRows.map((r) => r.bubbleSize);
  const colors = filteredRows.map((r) => -Math.log10(r.pValue));

  const text = filteredRows.map((r) => {
    const negLogP = (-Math.log10(r.pValue)).toFixed(2);
    return (
      `${r.pathway}` +
      `<br>Measured Genes: ${r.measured}` +
      `<br>Pathway Size: ${r.pathwaySize}` +
      `<br>Reach factor (Measured/Size): ${r.ratio.toFixed(3)}` +
      `<br>${colorMetric}: ${r.pValue}` +
      `<br>-log10(${colorMetric}): ${negLogP}`
    );
  });

  const maxSize = Math.max(...sizes);
  const sizeref =
    Number.isFinite(maxSize) && maxSize > 0 ? (2.0 * maxSize) / 30 ** 2 : 1;

  const maxRatio = Math.max(...xData);
  const xMax = Number.isFinite(maxRatio) ? maxRatio + 0.1 : 1;

  const nPathways = filteredRows.length;

// Make plot taller when many pathways
const rowHeight = 22; // px per pathway, tweak as you like
const layoutHeight = Math.max(420, Math.min(900, nPathways * rowHeight));

  const plotComponent = (
    <Plot
      data={[
        {
          x: xData,
          y: yData,
          mode: "markers",
          type: "scatter",
          text,
          hoverinfo: "text",
          marker: {
            size: sizes,
            sizemode: "area",
            sizeref,
            sizemin: 4,
            color: colors,
            colorscale: colorScale,
            colorbar: {
              title: "-log10(" + colorMetric + ")",
              titleside: "right",
            },
            showscale: true,
            opacity: 0.7,
            line: {
              width: 1,
              color: "white",
            },
          },
        },
      ]}
      layout={{
        title: { text: title, font: { size: 14 } },
        autosize: true,
        height: layoutHeight,
        paper_bgcolor: "white",
        plot_bgcolor: "white",
        margin: { l: 280, r: 60, t: 45, b: 65 },
        xaxis: {
          title: "Reach factor (Measured Genes / Pathway Size)",
          type: "linear",
          range: [0, xMax],
          zeroline: true,
        },
        yaxis: {
          title: yMetric,
          type: "category",
          categoryorder: "array",
          categoryarray: Array.from(new Set(yData)),
          automargin: true,  
        },
      }}
      config={{
        displayModeBar: "hover",
        responsive: true,
      }}
      style={{ width: "100%", height: "100%", minHeight: "420px" }}
      className={plotRef ? plotRef.replace("#", "") : "plotly-plot"}
    />
  );

  return (
    <BasePlot
      title={title}
      className={className}
      showColorPicker={false}
      downloadFilename="bubble_plot"
    >
      {plotComponent}
    </BasePlot>
  );
};

export default BubblePlot;
